<template>
  <div>
    <div
      class="card-header bg-white d-flex justify-content-between align-items-center py-3 cursor-pointer"
      :class="isCommercialExpanded ? '' : ' mb-4'"
      style="cursor: pointer"
      @click="isCommercialExpanded = !isCommercialExpanded"
    >
      <h6 class="m-0 font-weight-bold text-primary">Commercial Yachts</h6>
      <button
        type="button"
        class="btn btn-sm btn-link text-decoration-none p-0"
      >
        <i
          class="fas"
          :class="isCommercialExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
        ></i>
      </button>
    </div>
    <!-- Vessel Breakdown Categories Chart -->
    <div v-if="isCommercialExpanded" class="d-flex gap-1">
      <chart-card
        :show-chart-types="['line', 'bar']"
        :datasets="vesselCategoryDatasets"
        label="Vessel Counts by Category"
        size="col-md-6"
      ></chart-card>

      <!-- Gross Tonnage (GT) Total Chart -->
      <chart-card
        :show-chart-types="['line', 'bar']"
        :datasets="vesselGtDatasets"
        label="GT Total"
        size="col-md-6"
      ></chart-card>
    </div>
    <div class="text-danger mt-2" v-if="ignoredVessels.length > 0">
      {{ this.ignoredVessels.length }} vessels were ignored due to missing or
      invalid length and GT data. Please review the following vessels:
      <p>{{ this.ignoredVessels.join(", ") }}</p>
    </div>
  </div>
</template>

<script>
import ChartCard from "@/components/ui/ChartCard.vue";

export default {
  name: "VesselMetricsChart",
  components: { ChartCard },
  data() {
    return {
      isLoadingVessels: true,
      vessels: [],
      ignoredVessels: [],
      isCommercialExpanded: false,
      // Static historical baseline data from Kurt email (2026-10-05 8:11 AM).
      staticVesselKpis: [
        {
          year: "2022",
          under24m: 76,
          above24mBelow500GT: 76,
          above500GT: 9,
          total: 161,
          gtTotal: 36249,
        },
        {
          year: "2023",
          under24m: 74,
          above24mBelow500GT: 82,
          above500GT: 9,
          total: 165,
          gtTotal: 36752,
        },
        {
          year: "2024",
          under24m: 80,
          above24mBelow500GT: 93,
          above500GT: 10,
          total: 183,
          gtTotal: 43584.8,
        },
        {
          year: "2025",
          under24m: 94,
          above24mBelow500GT: 102,
          above500GT: 13,
          total: 209,
          gtTotal: 51176.4,
        },
      ],
    };
  },
  computed: {
    // Computes current active fleet counts dynamically from API vessels
    currentVesselKpis() {
      let under24m = 0;
      let above24mBelow500GT = 0;
      let above500GT = 0;
      let gtTotal = 0;
      //Filter vessels to only include commercial and pleasure yachts(commercial compliance) (vessel_type 2 and 3).
      const fileteredVessels = this.vessels.filter(
        (vessel) => vessel.vessel_type === 2 || vessel.vessel_type === 3
      );

      fileteredVessels.forEach((vessel) => {
        const rawLength = vessel.length || vessel.length_overall;
        const rawGt = vessel.gt || vessel.gt_and_nt;

        const parsedLength = Number(rawLength);
        const parsedGt = Number(rawGt);

        const hasValidLength = !isNaN(parsedLength) && parsedLength > 0;
        const hasValidGt = !isNaN(parsedGt) && parsedGt > 0;

        if (!hasValidLength || !hasValidGt) {
          this.ignoredVessels.push(
            vessel.name || vessel.id || "Unknown Vessel"
          );
          return;
        }

        gtTotal += parsedGt;

        // Categorization
        if (parsedLength < 24) {
          under24m += 1;
        } else if (parsedGt < 500) {
          above24mBelow500GT += 1;
        } else {
          above500GT += 1;
        }
      });

      const total = under24m + above24mBelow500GT + above500GT;
      if (total !== fileteredVessels.length) {
        console.warn(
          `Discrepancy in vessel counts: total (${total}) does not match filtered vessels length (${fileteredVessels.length}).`
        );
      }

      return {
        year: "Current",
        under24m,
        above24mBelow500GT,
        above500GT,
        total: fileteredVessels.length,
        gtTotal: Number(gtTotal.toFixed(1)),
      };
    },

    // Combines static years and the "Current" live entry
    allVesselKpis() {
      return [...this.staticVesselKpis, this.currentVesselKpis];
    },

    // Order of X-Axis labels (2022, 2023, 2024, 2025, Current)
    xAxisLabels() {
      return this.allVesselKpis.map((item) => item.year);
    },

    // Category Breakdown & Totals Dataset
    vesselCategoryDatasets() {
      const seriesConfigs = [
        { key: "under24m", label: "Under 24 m" },
        { key: "above24mBelow500GT", label: "Above 24 m below 500 GT" },
        { key: "above500GT", label: "Above 500 GT" },
        { key: "total", label: "Total" },
      ];

      return seriesConfigs.map((series) => ({
        label: series.label,
        data: this.xAxisLabels.map((xLabel) => {
          const row = this.allVesselKpis.find((item) => item.year === xLabel);
          return {
            type: xLabel, // X-Axis reference label ('2022', '2023', ..., 'Current')
            number: row ? row[series.key] : 0,
          };
        }),
      }));
    },

    // Gross Tonnage (GT) Total Dataset
    vesselGtDatasets() {
      return [
        {
          label: "GT Total",
          data: this.xAxisLabels.map((xLabel) => {
            const row = this.allVesselKpis.find((item) => item.year === xLabel);
            return {
              type: xLabel,
              number: row ? row.gtTotal : 0,
            };
          }),
        },
      ];
    },
  },
  async mounted() {
    await this.fetchVessels();
  },
  methods: {
    async fetchVessels() {
      this.isLoadingVessels = true;
      const filters = ["inService=true", "vesselTypeText=commercial,pleasure"];
      try {
        const { data } = await this.$store.dispatch(
          "vesselStore/fetchVesselsWithCache",
          {
            filters: filters ? [filters] : [],
          }
        );
        this.vessels = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error("Failed to load vessels: " + err.message);
      } finally {
        this.isLoadingVessels = false;
      }
    },
  },
};
</script>
